/**
 * Review — wireframe 2 (W1-18): the queue, the row detail, suggestion
 * acceptance, verdict chips.
 *
 * The screen renders verdicts; it decides nothing (ADR-0014). A chip's text
 * is the service's failing-check name; an Accept button posts a recorded
 * human acceptance (D-16); a correction posts the new value and lets the
 * service compute what that releases.
 */

import { Alert, Button, Card, buttonClass } from '@fiveox/ui'
import {
  BadgeCheck,
  Check,
  Eye,
  EyeOff,
  FileDown,
  ListChecks,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { DOCUMENT, useEvaluation } from '../lib/access'
import {
  ApiError,
  type DocumentRecord,
  type ReviewBody,
  documents,
  review,
} from '../lib/api'
import { useClient } from './ClientLayout'
import { HsnQueueCard } from './HsnQueueCard'

const CHIP_LABELS: Record<string, string> = {
  gstin_checksum: 'GSTIN fails checksum',
  taxable_missing: 'No taxable value',
  tax_math: 'Tax ≠ taxable × rate',
  // Same arithmetic, different instruction: the one above says the producer
  // disagreed with itself, this one says your own edit contradicts the row.
  tax_math_after_correction: 'Your edit ≠ the rest of this row',
  uqc_missing: 'UQC needed',
  uqc_unknown_unit: 'Unknown unit',
  hsn_unrepairable: 'HSN unrepairable',
  // Valid, and still not reportable: a 4-digit heading where this
  // taxpayer's turnover band requires six (X-09).
  hsn_heading_only: 'HSN too short to report',
  // A code was confirmed for this product before, and it does not sit
  // under the heading this line carries. Never swapped silently.
  hsn_memory_disagrees: 'Confirmed code disagrees',
  state_code_mismatch: 'State ≠ GSTIN prefix',
}

/**
 * The verdict chip. The legacy's colour semantics — green ready, amber needs a
 * human, red blocking — over the service's own words, which are never
 * rewritten into a bucket.
 */
function chip(text: string, tone: 'hold' | 'ok' | 'conflict') {
  const tones = { ok: 'is-ok', hold: 'is-hold', conflict: 'is-stop' }
  return <span className={`gst-chip ${tones[tone]}`}>{text}</span>
}

export function Review() {
  const client = useClient()
  const { can } = useEvaluation()

  const [queue, setQueue] = useState<DocumentRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<ReviewBody | null>(null)
  // Which page of rows is on screen. Reset whenever the document does —
  // page 9 of a 40,568-line register means nothing in a 12-line one.
  const [offset, setOffset] = useState(0)
  const PAGE = 200
  const [error, setError] = useState<string | null>(null)
  const [correcting, setCorrecting] = useState<{ key: string; field: string; value: string } | null>(
    null,
  )
  const [reclassifying, setReclassifying] = useState(false)
  const [remark, setRemark] = useState<string | null>(null)
  const [money, setMoney] = useState<{
    key: string
    values: Record<string, string>
    reason: string
    note: string
  } | null>(null)
  const [excluding, setExcluding] = useState<{
    key: string
    reason: string
    note: string
  } | null>(null)
  const [adding, setAdding] = useState<{
    values: Record<string, string>
    reason: string
    note: string
  } | null>(null)
  const [sourceOpen, setSourceOpen] = useState(false)
  /**
   * The minted capability. Held in state and NEVER in an href on load — it is
   * a bearer URL with no tenant check behind it, live for five minutes.
   */
  const [source, setSource] = useState<{ url: string; render: string } | null>(null)
  const [sourceError, setSourceError] = useState<string | null>(null)

  const loadQueue = useCallback(async () => {
    try {
      const all = (await documents.list({ client_id: client.id })).data
      // The queue: anything a reviewer can act on — staged for review work,
      // or held for extraction review. One list, no silent bucket.
      const reviewable = all.filter(
        (d) =>
          d.stage === 'mapping_review' ||
          d.stage === 'enrichment' ||
          d.held === 'review_extraction',
      )
      setQueue(reviewable)
      setSelected((current) => current ?? reviewable[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The review queue could not be loaded.')
    }
  }, [client.id])

  const loadDetail = useCallback(async () => {
    if (!selected) {
      setBody(null)
      return
    }
    try {
      setBody((await review.get(selected, { limit: PAGE, offset })).data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The document could not be loaded.')
    }
  }, [selected, offset])

  useEffect(() => {
    void loadQueue()
  }, [loadQueue])
  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  // Close the remark box when the selection moves. A half-typed sentence about
  // one invoice must not be left sitting over the next one.
  useEffect(() => {
    setRemark(null)
    setMoney(null)
    setExcluding(null)
    setAdding(null)
  }, [selected])

  /**
   * Drop the previous document's URL the instant the selection moves. A stale
   * capability still renders — it would show the PREVIOUS invoice in a pane
   * captioned with this one's name, which is the worst bug this feature can
   * have. Clearing first and minting second makes that impossible.
   */
  useEffect(() => {
    setSource(null)
    setSourceError(null)
  }, [selected])

  const openSource = useCallback(async () => {
    if (!selected) return
    try {
      const body = (await documents.sourceUrl(selected)).data
      setSource({ url: body.url, render: body.render })
      setSourceError(null)
    } catch (err) {
      setSource(null)
      setSourceError(
        err instanceof ApiError ? err.message : 'The source could not be opened.',
      )
    }
  }, [selected])

  /**
   * Mint when the pane is open and there is nothing to show — on opening it,
   * and after a selection change cleared the last one.
   *
   * Deliberately NOT on a timer. The URL expires in five minutes; refreshing it
   * unattended would mint capabilities and write "viewed this invoice" audit
   * rows all afternoon for a tab nobody is looking at. Expiry shows a Reload.
   */
  useEffect(() => {
    if (sourceOpen && !source && !sourceError) void openSource()
  }, [sourceOpen, source, sourceError, openSource])

  const selectedDoc = useMemo(
    () => queue.find((d) => d.id === selected) ?? null,
    [queue, selected],
  )

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null)
      try {
        await fn()
        await Promise.all([loadDetail(), loadQueue()])
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      }
    },
    [loadDetail, loadQueue],
  )

  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <ListChecks size={20} /> Raw Data — review
        </h1>
        <p className="gst-sub">
          Rows hold with their failing checks named; a fix is what releases them
        </p>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {/*
        * Client-scoped, so it sits above the per-document queue rather than
        * inside it: the sheet answers a product once for the whole taxpayer,
        * not once per register it happens to appear in.
        */}
      <HsnQueueCard
        clientId={client.id}
        gstin={client.gstin}
        onApplied={() => {
          void loadQueue()
          void loadDetail()
        }}
      />

      <div className="flex flex-wrap gap-6">
        {/* -- the queue ---------------------------------------------------- */}
        <aside className="w-64 shrink-0">
          {queue.length === 0 ? (
            <div className="gst-empty">Nothing to review.</div>
          ) : (
            <ul className="space-y-1">
              {queue.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(doc.id)
                      setOffset(0)
                    }}
                    aria-current={doc.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="block truncate">{doc.source_filename}</span>
                    <span className="mt-0.5 block text-[11px]">
                      {doc.held === 'review_extraction'
                        ? 'Review first'
                        : doc.stage?.replace('_', ' ')}
                      {' · '}
                      {doc.period}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* -- the detail --------------------------------------------------- */}
        {/*
          * `basis` is what keeps the table readable: with `min-w-0 flex-1` on
          * this and a flex-1 pane beside it, the SECTION is the side that
          * collapses — at 1440px it was squeezed to ~150px while the pane took
          * 560. It needs a basis to grow from, and the pane needs a fixed width
          * so it competes for nothing.
          */}
        <section className="min-w-0 flex-1 basis-[520px]">
          {!body || !selectedDoc ? (
            <p className="text-sm text-muted-foreground">Select a document.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {selectedDoc.source_filename}
                  </p>
                  <p className="gst-meta">
                    {selectedDoc.doc_type ?? 'unclassified'} · parsed by{' '}
                    {selectedDoc.producer}@{selectedDoc.producer_version}
                    {selectedDoc.checksum_ok === false && ' · producer checksum FAILED'}
                  </p>
                </div>
                {/*
                  * A document ingested before originals were retained has no
                  * source and never will — the de-duplication rule means a
                  * re-upload cannot supply one. Say that, rather than showing
                  * nothing: an absent button explains nothing, and "missing"
                  * would send someone hunting for a file that never existed.
                  */}
                {selectedDoc.source && !selectedDoc.source.available && (
                  <p className="gst-meta">
                    No source file was kept — uploaded before originals were retained.
                  </p>
                )}
                {selectedDoc.verified && (
                  <p className="gst-meta">
                    <BadgeCheck size={12} className="inline" /> Verified{' '}
                    {new Date(selectedDoc.verified.at).toLocaleString()} —{' '}
                    {selectedDoc.verified.remark}
                  </p>
                )}
                {can(DOCUMENT, 'can_view') && selectedDoc.source?.available && (
                  <Button
                    type="button"
                    onClick={() => setSourceOpen((v) => !v)}
                    variant="outline" size="sm"
                  >
                    {sourceOpen ? <EyeOff size={13} /> : <Eye size={13} />}
                    {sourceOpen ? 'Hide document' : 'Show document'}
                  </Button>
                )}
                {can(DOCUMENT, 'can_update') && remark === null && (
                  <Button type="button" onClick={() => setRemark('')} variant="outline" size="sm">
                    <BadgeCheck size={13} />
                    {selectedDoc.verified ? 'Verify again' : 'Verify'}
                  </Button>
                )}
                {can(DOCUMENT, 'can_update') && (
                  <div className="flex items-center gap-2">
                    {reclassifying ? (
                      <>
                        {['sales_report', 'invoice'].map((kind) => (
                          <Button
                            key={kind}
                            type="button"
                            onClick={() =>
                              void act(() => documents.reclassify(selectedDoc.id, { doc_type: kind }))
                            }
                            variant="outline" size="sm"
                          >
                            as {kind.replace('_', ' ')}
                          </Button>
                        ))}
                        <Button
                          type="button"
                          onClick={() => setReclassifying(false)}
                          variant="outline" size="sm"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => setReclassifying(true)}
                        variant="outline" size="sm"
                      >
                        <RefreshCw size={13} /> Reclassify & resubmit
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* suggestions (D-16: a person accepts, or nothing happens) */}
              {body.suggestions.length > 0 && can(DOCUMENT, 'can_update') && (
                <Alert tone="warning" className="mb-4">
                  <p className="gst-panel-h">Suggestions — nothing applies until you accept it</p>
                  <ul className="space-y-1">
                    {body.suggestions.map((s) => (
                      <li
                        key={`${s.content_key}:${s.field}:${s.rule_id}`}
                        className="flex flex-wrap items-center gap-2 text-sm text-foreground"
                      >
                        <span>
                          {s.field} → <strong>{String(s.value)}</strong>
                          <span className="text-xs text-muted-foreground"> ({s.reason})</span>
                          {s.computed && (
                            <span className="text-xs text-muted-foreground"> · computed</span>
                          )}
                        </span>
                        <Button
                          type="button"
                          onClick={() =>
                            void act(() =>
                              review.accept(selectedDoc.id, {
                                content_key: s.content_key,
                                field: s.field,
                                value: s.value,
                                rule_id: s.rule_id,
                                kind: s.kind === 'classification' ? 'classification' : 'enrichment',
                                computed: s.computed,
                                hsn:
                                  s.field === 'uqc'
                                    ? (body.rows.find((r) => r.content_key === s.content_key)
                                        ?.effective.hsn as string | undefined)
                                    : undefined,
                                // The mirror of `hsn` above: a UQC acceptance
                                // teaches a master keyed by HSN, an HSN
                                // acceptance teaches one keyed by the product.
                                item:
                                  s.field === 'hsn'
                                    ? (body.rows.find((r) => r.content_key === s.content_key)
                                        ?.effective.item as string | undefined)
                                    : undefined,
                              }),
                            )
                          }
                          variant="outline" size="sm"
                        >
                          <Check size={12} /> Accept
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/*
                * The remark box. NOT pre-filled, and that is a deliberate
                * departure from the legacy, which pre-fills "I have reviewed
                * and verified this invoice." — a confirmation you satisfy by
                * pressing nothing is weaker than one you satisfy by pressing
                * the space bar, and the server refuses that one. Asking a
                * person to write what they checked is the entire value here.
                */}
              {remark !== null && (
                <Card className="mb-4 p-3">
                  <label className="gst-meta mb-1 block" htmlFor="verify-remark">
                    What did you check?
                  </label>
                  <textarea
                    id="verify-remark"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value.slice(0, 500))}
                    placeholder="e.g. Agrees with the invoice; buyer GSTIN corrected."
                    className="gst-input min-h-16 w-full"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      type="button"
                      disabled={!remark.trim()}
                      onClick={() =>
                        void act(async () => {
                          await review.verify(selectedDoc.id, { remark })
                          setRemark(null)
                        })
                      }
                      variant="outline" size="sm"
                    >
                      <Check size={13} /> Save &amp; verify
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setRemark(null)}
                      variant="outline" size="sm"
                    >
                      Cancel
                    </Button>
                    <span className="gst-meta">{500 - remark.length} left</span>
                  </div>
                </Card>
              )}

              {/*
                * What the whole document holds, above a page of it. These are
                * the service's tallies over every row, never a count of the
                * array below — paging that hid the scale would be worse than
                * the payload it was shrinking.
                */}
              {body.total_rows > body.rows.length && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="gst-meta">
                    Rows {body.offset + 1}&ndash;
                    {Math.min(body.offset + body.rows.length, body.total_rows)} of{' '}
                    {body.total_rows.toLocaleString()}
                  </span>
                  {Object.entries(body.flag_counts).map(([reason, n]) =>
                    chip(
                      `${n.toLocaleString()} ${CHIP_LABELS[reason] ?? reason}`,
                      'hold',
                    ),
                  )}
                  <span className="ml-auto flex gap-2">
                    <Button
                      type="button" variant="outline" size="sm"
                      disabled={body.offset === 0}
                      onClick={() => setOffset(Math.max(0, body.offset - PAGE))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      disabled={body.offset + body.rows.length >= body.total_rows}
                      onClick={() => setOffset(body.offset + PAGE)}
                    >
                      Next
                    </Button>
                  </span>
                </div>
              )}

              {/* the rows, verdict-chipped */}
              {body.rows.length === 0 ? (
                <div className="gst-empty">
                  The extraction carries no rows —{' '}
                  {selectedDoc.held === 'review_extraction'
                    ? 'reclassify and resubmit, or send a readable copy.'
                    : 'nothing to review here.'}
                </div>
              ) : (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Invoice</th>
                        <th className="is-text">Buyer</th>
                        <th>Taxable (₹)</th>
                        <th className="is-text">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {body.rows.map((row, i) => (
                        <tr key={row.content_key} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text is-sec">
                            {String(row.effective.invoice_no ?? '—')}
                            <small>{String(row.effective.invoice_date ?? '')}</small>
                          </td>
                          <td className="is-text">
                            {String(row.effective.buyer_name ?? '—')}
                            <span className="gst-mono block">
                              {String(row.effective.gstin_buyer ?? '')}
                            </span>
                          </td>
                          <td>
                            {typeof row.effective.taxable === 'number'
                              ? (row.effective.taxable / 100).toLocaleString('en-IN', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : '—'}
                          </td>
                          <td className="is-text">
                            <span className="flex flex-wrap items-center gap-1">
                              {row.accepted && chip('clean', 'ok')}
                              {row.failing.map((name) =>
                                chip(CHIP_LABELS[name] ?? name, 'hold'),
                              )}
                              {row.conflicts.map((c, i) => (
                                <span key={i}>{chip(`conflict: ${String(c.field)}`, 'conflict')}</span>
                              ))}
                              {body.flags
                                .filter((f) => f.content_key === row.content_key)
                                .map((f) => chip(CHIP_LABELS[f.reason] ?? f.reason, 'hold'))}
                              {row.excluded_by && chip('removed by you', 'conflict')}
                              {row.added_by && chip('added by you', 'ok')}
                            </span>
                            {/*
                              * An excluded row stays in this table on purpose.
                              * If the screen showed only live rows there would
                              * be no way back, and an append-only design would
                              * be undone by an interface with no undo.
                              */}
                            {row.excluded_by && (
                              <p className="gst-meta mt-1">
                                {row.excluded_by.note}{' '}
                                {can(DOCUMENT, 'can_update') && (
                                  <button
                                    type="button"
                                    className="gst-link"
                                    onClick={() =>
                                      void act(() =>
                                        review.exclude(selectedDoc.id, {
                                          content_key: row.content_key,
                                          excluded: false,
                                          reason_code: 'other',
                                          note: 'Put back into the return.',
                                        }),
                                      )
                                    }
                                  >
                                    Put it back
                                  </button>
                                )}
                              </p>
                            )}
                            {can(DOCUMENT, 'can_update') &&
                              !row.excluded_by &&
                              money?.key !== row.content_key &&
                              excluding?.key !== row.content_key && (
                                <span className="mt-1 flex gap-2">
                                  <button
                                    type="button"
                                    className="gst-link"
                                    onClick={() =>
                                      setMoney({
                                        key: row.content_key,
                                        values: {},
                                        reason: '',
                                        note: '',
                                      })
                                    }
                                  >
                                    Correct amounts
                                  </button>
                                  <button
                                    type="button"
                                    className="gst-link"
                                    onClick={() =>
                                      setExcluding({
                                        key: row.content_key,
                                        reason: '',
                                        note: '',
                                      })
                                    }
                                  >
                                    Take it out
                                  </button>
                                </span>
                              )}
                            {money?.key === row.content_key && (
                              <Card className="mt-2 p-2">
                                <div className="flex flex-wrap gap-2">
                                  {[...body.money_fields, ...body.rate_fields].map((f) => (
                                    <label key={f} className="gst-meta">
                                      {f}
                                      <input
                                        value={money.values[f] ?? ''}
                                        onChange={(e) =>
                                          setMoney({
                                            ...money,
                                            values: {
                                              ...money.values,
                                              [f]: e.target.value,
                                            },
                                          })
                                        }
                                        placeholder={
                                          typeof row.effective[f] === 'number' &&
                                          body.money_fields.includes(f)
                                            ? String((row.effective[f] as number) / 100)
                                            : String(row.effective[f] ?? '')
                                        }
                                        className="gst-input w-24"
                                      />
                                    </label>
                                  ))}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <select
                                    value={money.reason}
                                    onChange={(e) =>
                                      setMoney({ ...money, reason: e.target.value })
                                    }
                                    className="gst-select"
                                  >
                                    <option value="">Why?</option>
                                    {Object.entries(body.money_reasons).map(
                                      ([code, label]) => (
                                        <option key={code} value={code}>
                                          {label}
                                        </option>
                                      ),
                                    )}
                                  </select>
                                  <input
                                    value={money.note}
                                    onChange={(e) =>
                                      setMoney({ ...money, note: e.target.value.slice(0, 500) })
                                    }
                                    placeholder="What does the document say?"
                                    className="gst-input min-w-56 flex-1"
                                  />
                                  <Button
                                    type="button"
                                    disabled={
                                      !money.reason ||
                                      !money.note.trim() ||
                                      !Object.values(money.values).some((v) => v.trim())
                                    }
                                    onClick={() =>
                                      void act(async () => {
                                        await review.correctMoney(selectedDoc.id, {
                                          content_key: row.content_key,
                                          values: Object.fromEntries(
                                            Object.entries(money.values).filter(
                                              ([, v]) => v.trim(),
                                            ),
                                          ),
                                          reason_code: money.reason,
                                          note: money.note,
                                        })
                                        setMoney(null)
                                      })
                                    }
                                    variant="outline" size="sm"
                                  >
                                    Save amounts
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline" size="sm"
                                    onClick={() => setMoney(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </Card>
                            )}
                            {excluding?.key === row.content_key && (
                              <Card className="mt-2 flex flex-wrap items-center gap-2 p-2">
                                <select
                                  value={excluding.reason}
                                  onChange={(e) =>
                                    setExcluding({ ...excluding, reason: e.target.value })
                                  }
                                  className="gst-select"
                                >
                                  <option value="">Why?</option>
                                  {Object.entries(body.money_reasons).map(
                                    ([code, label]) => (
                                      <option key={code} value={code}>
                                        {label}
                                      </option>
                                    ),
                                  )}
                                </select>
                                <input
                                  value={excluding.note}
                                  onChange={(e) =>
                                    setExcluding({
                                      ...excluding,
                                      note: e.target.value.slice(0, 500),
                                    })
                                  }
                                  placeholder="The client will see this on the workbook"
                                  className="gst-input min-w-56 flex-1"
                                />
                                <Button
                                  type="button"
                                  disabled={!excluding.reason || !excluding.note.trim()}
                                  onClick={() =>
                                    void act(async () => {
                                      await review.exclude(selectedDoc.id, {
                                        content_key: row.content_key,
                                        excluded: true,
                                        reason_code: excluding.reason,
                                        note: excluding.note,
                                      })
                                      setExcluding(null)
                                    })
                                  }
                                  variant="outline" size="sm"
                                >
                                  {/*
                                    * NOT "Take it out" again. That is the
                                    * label on the button that OPENED this form,
                                    * so the same words appeared twice for two
                                    * different acts — one that asks and one
                                    * that does. The confirm says what it
                                    * confirms.
                                    */}
                                  Remove this row
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline" size="sm"
                                  onClick={() => setExcluding(null)}
                                >
                                  Cancel
                                </Button>
                              </Card>
                            )}
                            {!row.accepted && can(DOCUMENT, 'can_update') && (
                              <span className="mt-1 block">
                                {correcting?.key === row.content_key ? (
                                  <span className="flex flex-wrap items-center gap-1">
                                    <select
                                      value={correcting.field}
                                      onChange={(e) =>
                                        setCorrecting({ ...correcting, field: e.target.value })
                                      }
                                      className="gst-select"
                                    >
                                      {(body.correctable_fields ?? []).map((f) => (
                                        <option key={f} value={f}>
                                          {f}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      value={correcting.value}
                                      onChange={(e) =>
                                        setCorrecting({ ...correcting, value: e.target.value })
                                      }
                                      placeholder="new value"
                                      className="gst-input w-36"
                                    />
                                    <Button
                                      type="button"
                                      disabled={!correcting.value.trim()}
                                      onClick={() =>
                                        void act(() =>
                                          review.correct(selectedDoc.id, {
                                            content_key: row.content_key,
                                            field: correcting.field,
                                            value: correcting.value.trim(),
                                          }),
                                        ).then(() => setCorrecting(null))
                                      }
                                      variant="outline" size="sm"
                                    >
                                      Apply
                                    </Button>
                                    <Button
                                      type="button"
                                      onClick={() => setCorrecting(null)}
                                      variant="outline" size="sm"
                                    >
                                      Cancel
                                    </Button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCorrecting({
                                        key: row.content_key,
                                        field: row.failing.includes('gstin_checksum')
                                          ? 'gstin_buyer'
                                          : 'uqc',
                                        value: '',
                                      })
                                    }
                                    className="gst-link"
                                  >
                                    Correct…
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/*
                * STAGE 4 — the only control on this screen that can create
                * money from nothing, which is why it sits BELOW the table
                * rather than beside a row: it is not an edit to anything, and
                * putting it among the per-row actions would make it look like
                * one. The note is required and the CLIENT READS IT, on its own
                * sheet in the workbook.
                */}
              {/*
                * GATED ON THE APPROVAL TRACK, and on nothing else.
                *
                * Two wrong gates were tried first, and the second was worse.
                * `rows.length > 0` hid the control on a document the parser
                * read NOTHING from — which is the canonical case for it,
                * "page 3 was unreadable". Removing that gate put it on HELD
                * documents, whose rows never reach the return at all: the row
                * saved, the screen showed it as clean, and the client's return
                * silently did not contain it.
                *
                * A held document's way forward is the one already on screen —
                * reclassify, or send a readable copy. The service refuses this
                * too; a gate that lives only in the interface is a gate
                * somebody routes around.
                */}
              {can(DOCUMENT, 'can_update') && body.can_add_rows && (
                <div className="mt-4">
                  {adding === null ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAdding({ values: {}, reason: '', note: '' })}
                    >
                      <Plus size={13} /> Add a row the parser missed
                    </Button>
                  ) : (
                    <Card className="p-3">
                      <p className="gst-meta mb-2">
                        A row no document produced. It goes through the same checks as
                        every other row, and <strong>the client&rsquo;s workbook names
                        it</strong> with the reason you give.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {body.addable_fields.map((f) => (
                          <label key={f} className="gst-meta">
                            {f}
                            <input
                              value={adding.values[f] ?? ''}
                              onChange={(e) =>
                                setAdding({
                                  ...adding,
                                  values: { ...adding.values, [f]: e.target.value },
                                })
                              }
                              className="gst-input w-28"
                            />
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <select
                          value={adding.reason}
                          onChange={(e) => setAdding({ ...adding, reason: e.target.value })}
                          className="gst-select"
                        >
                          <option value="">Why?</option>
                          {Object.entries(body.money_reasons).map(([code, label]) => (
                            <option key={code} value={code}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={adding.note}
                          onChange={(e) =>
                            setAdding({ ...adding, note: e.target.value.slice(0, 500) })
                          }
                          placeholder="Where did this row come from? The client will see this"
                          className="gst-input min-w-72 flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            !adding.reason ||
                            !adding.note.trim() ||
                            !Object.values(adding.values).some((v) => v.trim())
                          }
                          onClick={() =>
                            void act(async () => {
                              await review.addRow(selectedDoc.id, {
                                line: Object.fromEntries(
                                  Object.entries(adding.values).filter(([, v]) => v.trim()),
                                ),
                                reason_code: adding.reason,
                                note: adding.note,
                              })
                              setAdding(null)
                            })
                          }
                        >
                          Add the row
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAdding(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {body.orphaned.length > 0 && (
                <Card className="mt-3 p-3 text-xs text-muted-foreground">
                  {body.orphaned.length === 1
                    ? 'One earlier edit no longer matches any row'
                    : `${body.orphaned.length} earlier edits no longer match any row`}{' '}
                  — the re-extraction changed those rows; review their fields:{' '}
                  {body.orphaned.map((o) => o.field).join(', ')}.
                </Card>
              )}
            </>
          )}
        </section>

        {/* -- the document, beside the values ------------------------------ */}
        {sourceOpen && selectedDoc && (
          <aside className="w-[460px] shrink-0">
            <Card className="sticky top-4 flex h-[calc(100vh-8rem)] flex-col p-3">
              <p className="gst-meta mb-2 shrink-0 truncate">
                {selectedDoc.source_filename}
              </p>
              {sourceError ? (
                <div className="gst-empty">
                  {sourceError}{' '}
                  <button type="button" className="gst-link" onClick={() => void openSource()}>
                    Reload
                  </button>
                </div>
              ) : !source ? (
                <div className="gst-empty">Opening…</div>
              ) : source.render === 'pdf' ? (
                <iframe
                  title={selectedDoc.source_filename ?? 'Uploaded document'}
                  src={source.url}
                  referrerPolicy="no-referrer"
                  className="min-h-0 flex-1 rounded border-0"
                />
              ) : source.render === 'image' ? (
                <div className="min-h-0 flex-1 overflow-auto rounded bg-muted/30">
                  <img
                    alt={selectedDoc.source_filename ?? 'Uploaded document'}
                    src={source.url}
                    referrerPolicy="no-referrer"
                    className="mx-auto block"
                  />
                </div>
              ) : (
                /*
                 * `none` — a register. NEVER an iframe: a spreadsheet served
                 * inline makes the browser DOWNLOAD it, which inside the app
                 * looks like a spontaneous unexplained download. The
                 * predecessor shipped exactly that bug. And a register is not
                 * read anyway — it is compared, and the comparison surface is
                 * the table already on the left.
                 */
                <div className="gst-empty flex-1">
                  <p className="mb-2">
                    {selectedDoc.source?.content_type?.includes('spreadsheet') ||
                    selectedDoc.source?.content_type?.includes('ms-excel')
                      ? 'An Excel workbook'
                      : selectedDoc.source?.content_type === 'text/csv'
                        ? 'A CSV register'
                        : 'This file type'}{' '}
                    cannot be shown here — the rows beside it are what to check.
                  </p>
                  <a
                    href={source.url}
                    download={selectedDoc.source_filename ?? undefined}
                    referrerPolicy="no-referrer"
                    className={buttonClass({ variant: 'outline', size: 'sm', className: 'inline-flex' })}
                  >
                    <FileDown size={13} /> Download
                  </a>
                </div>
              )}
            </Card>
          </aside>
        )}
      </div>
    </div>
  )
}
